import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, Trash2, ExternalLink, Eye, Search, Loader2, BarChart3, Shield } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { useProfile } from '@/hooks/useProfile';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface HostedSite {
  id: string;
  slug: string;
  site_name: string;
  description: string | null;
  user_id: string | null;
  views_count: number;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

const AdminHostedSites = () => {
  const { isAdmin, loading: profileLoading } = useProfile();
  const navigate = useNavigate();
  const [sites, setSites] = useState<HostedSite[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!profileLoading && !isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, profileLoading, navigate]);

  useEffect(() => {
    if (isAdmin) fetchSites();
  }, [isAdmin]);

  const fetchSites = async () => {
    setLoading(true);
    try {
      const { data, error } = await (supabase as any)
        .from('hosted_sites')
        .select('id, slug, site_name, description, user_id, views_count, is_published, created_at, updated_at')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSites((data as HostedSite[]) || []);
    } catch (e: any) {
      console.error('Error fetching hosted sites:', e);
      toast.error('Failed to load hosted sites');
    } finally {
      setLoading(false);
    }
  };

  const deleteSite = async (siteId: string) => {
    try {
      const { error } = await (supabase as any)
        .from('hosted_sites')
        .delete()
        .eq('id', siteId);

      if (error) throw error;

      toast.success('Site deleted');
      fetchSites();
    } catch (e: any) {
      toast.error(e.message || 'Failed to delete site');
    }
  };

  const filteredSites = sites.filter(s =>
    s.site_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.slug.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalViews = sites.reduce((sum, s) => sum + s.views_count, 0);

  if (profileLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary/20 via-primary/10 to-accent/20 p-6 border border-primary/20">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="relative flex items-center gap-4">
          <div className="p-3 rounded-2xl bg-gradient-to-br from-primary/30 to-primary/10 shadow-lg shadow-primary/20">
            <Shield className="h-7 w-7 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Hosted Sites Management</h1>
            <p className="text-muted-foreground text-sm mt-1">Admin control over all published sites</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-primary/10">
                <Globe className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{sites.length}</p>
                <p className="text-xs text-muted-foreground">Total Sites</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-green-500/10">
                <Eye className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalViews.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Total Views</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-amber-500/10">
                <BarChart3 className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{sites.filter(s => s.is_published).length}</p>
                <p className="text-xs text-muted-foreground">Live Sites</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sites Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle>All Hosted Sites</CardTitle>
              <CardDescription>{sites.length} sites total</CardDescription>
            </div>
            <div className="relative max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search sites..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                     <TableHead>Site</TableHead>
                     <TableHead>Owner</TableHead>
                     <TableHead>Slug</TableHead>
                     <TableHead>Views</TableHead>
                     <TableHead>Status</TableHead>
                     <TableHead>Created</TableHead>
                     <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSites.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                        No hosted sites found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSites.map((site) => (
                      <TableRow key={site.id}>
                        <TableCell className="font-medium">{site.site_name}</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">{site.user_id ? site.user_id.slice(0, 8) + '...' : 'N/A'}</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">/s/{site.slug}</TableCell>
                        <TableCell>{site.views_count.toLocaleString()}</TableCell>
                        <TableCell>
                          <Badge variant={site.is_published ? 'default' : 'secondary'} className="text-[10px]">
                            {site.is_published ? 'Live' : 'Draft'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {format(new Date(site.created_at), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => window.open(`/s/${site.slug}`, '_blank')}
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive">
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete "{site.site_name}"?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will permanently delete the site and all its analytics data.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => deleteSite(site.id)}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminHostedSites;
